import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { DropDownOption } from "@app/interfaces/IDropDown";
import { ConstantsService } from "@app/services/constants.service";
import { WorkflowRunService } from "@app/services/workflow-run.service";
import { Labels } from "@app/util/labels.util";
import tourData from "@app/assets/api-dummy-data.json";
import { TourGuideService } from "@app/services/tour-guide.service";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { CommonFunctionalitiesService } from "@app/services/common-functionalities.service";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { WfQueueManagerService } from "@app/services/wf-queue-manager.service";
import { WfFeaturesService } from "@app/services/wf-features.service";
import { MatTabGroup } from "@angular/material/tabs";
import { Messages } from "@app/src/util/messages";
import { TourGuideContent } from "@app/src/util/tour-guide-content.util";

@Component({
  selector: "app-workflow-run",
  templateUrl: "./workflow-run.component.html",
  styleUrls: ["./workflow-run.component.scss"],
})
export class WorkflowRunComponent implements OnInit {
  /****************** Variable declaration ******************/
  dataSource: any[] = [];
  dataSubscription: Subscription;
  wfRunDataSubscription: Subscription; // Subscription for getWfRunData to prevent race conditions

  rowData: any;
  stgDtl: any;
  wfType: any;
  qgDataSource: any = [];
  inputArgumentList: { key: string, value: any }[] = [];
  getNoData = Messages.no_selected_item; //for getting no data available message
  noData = Messages.no_data; //for getting no data available message

  /************************** dropdown options *********************************/
  workflowDropdown = this.constantService.WF_DEFAULT_OPT; // workflow dropdown
  durationDropdown = this.constantService.DURATION_DROPDOWN; // duration dropdown

  selectedrepo: DropDownOption = this.workflowDropdown[0]; // default selected workflow
  selectedDuration: DropDownOption = this.constantService.DURATION_DROPDOWN[0]; // default selected duration

  wfTitle: string = Labels.workflows;
  durationTitle: string = Labels.duration;

  wfIdWorkflows: any;
  cardClicked: boolean = true;
  filteredData: any[] = [];
  clear: boolean = false;
  searchValue: string = '';
  tourId = TourGuideContent;

  /**
   * Infinite scroll parameters
   * On page load 50 records will be initialized
   * If 50 records are scrolled then automatically it'll load more 50 data by changing the offset
   */
  offset = 0;
  limit = 50;
  isLoadingMore = false; // Flag to prevent multiple simultaneous API calls during scroll
  hasMoreData = true; // Flag to track if more data exists to load from backend

  /****************** View child ******************/
  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;

  /**
   * Constructor
   * @param constantService 
   * @param workflowRunService 
   * @param tourGuideService 
   * @param cf 
   * @param route 
   * @param wfQueueService 
   * @param featureWf 
   * @param activatedRoutes
   */
  constructor(
    private constantService: ConstantsService,
    private workflowRunService: WorkflowRunService,
    public tourGuideService: TourGuideService,
    public cf: CommonFunctionalitiesService,
    private route: Router,
    public wfQueueService: WfQueueManagerService,
    private featureWf: WfFeaturesService,
    private activatedRoutes: ActivatedRoute,
  ) {
    this.wfIdWorkflows = this.activatedRoutes.snapshot.params;

    // get workflow type from the repo name and pass the details to mat-card
    this.activatedRoutes.params.subscribe((event: any) => {
      if (this.workflowRunService.repo.length > 0) {
        this.addWfType(this.workflowRunService.repo);
      }
      this.workflowRunService.data$.subscribe((res) => {
        if (res) {
          this.addWfType(res);
        }
      });
    });
  }

  /**
   * Lifecycle hook called when the component is initialized.
   * - If the 'isStartTour' flag is true in the 'tourGuideService', populates the 'dataSource' with dummy data for the workflow table.
   * - Otherwise, calls 'getWfhNames' to fetch data and sets up route parameters to load workflow run data.
   */
  ngOnInit() {
    // Check if the 'isStartTour' flag is true in the 'tourGuideService'
    if (this.tourGuideService.isStartTour) {
      this.filteredData = this.dataSource = tourData.workflows.wfRun.wfrundata; //for the tourguide passing the dummy data for workflow table
      this.rowData = tourData.workflows.wfRun.runoverviewdata;
      this.qgDataSource = tourData.workflows.wfRun.quaitygates;
      this.stgDtlMapping();
      this.inputArgsMapping(tourData.workflows.wfRun.inputargs);
      return;
    }
    this.tourGuideService.dashboardIndex = 0;
    this.onDropdownSelect();
    this.getWfRunData();
  }

  /**
   * This method is called when the component is being destroyed to unsubscribe from the dataSubscription.
   */
  ngOnDestroy() {
    if (this.dataSubscription) {
      // Unsubscribe from the dataSubscription to prevent memory leaks.
      this.dataSubscription.unsubscribe();
    }
    if (this.wfRunDataSubscription) {
      // Cancel any pending workflow run data requests to prevent memory leaks
      this.wfRunDataSubscription.unsubscribe();
    }
  }

  /** On card click mat-tabs will be initialized and api's calls for the same are present in the function
   *  If tour guide is initialized then api's calls will be skipped
   */
  onCardClick($event: Event) {
    if (this.tourGuideService.isStartTour) {
      this.rowData = $event;
      this.tourGuideService.dashboardIndex = 0;
      return
    }

    this.rowData = $event;
    this.tourGuideService.dashboardIndex = 0;
    this.qgToGanttChart();
    this.inputArgs();
  }

  /**
   * add wf type function assigns the value event/manual
   * @param res 
   */
  addWfType(res: any) {
    this.wfType = res.find((el: any) => el.value === parseInt(this.wfIdWorkflows.wfId))?.type || "";
  }

  /** 
   * onScroll function will be triggered when user scroll through the cards and as it nears to the end
   * of the scoll api will be called to fetch new set of data
   */
  onScroll(element = this.scrollContainer.nativeElement): void {
    const threshold = 50; // Load when within 50px of bottom (buffer zone)
    if (element.scrollTop + element.offsetHeight >= element.scrollHeight - threshold && !this.isLoadingMore && this.hasMoreData) {
      this.getWfRunData();
    }
  }

  /**
   * filter displayed workflows according to search input
   * @param input search query
   */
  filterWfs(input: string) {
    input = input.toLowerCase();
    this.rowData = null;
    this.filteredData = this.dataSource?.slice().filter(data => {
      return data?.workTicketId.toLowerCase().includes(input) ||
        data?.author.toLowerCase().includes(input) ||
        data?.repoName.toLowerCase().includes(input)
    });
  }

  /** searchClear function is triggered when the cancel icon inside the search box is clicked,
   *  on click of the icon the input should be cleared and the original data should be visible
   */
  searchClear() {
    if (this.searchValue) {
      this.searchValue = '';
      this.rowData = null;
      this.clear = true;
      this.filteredData = [...this.dataSource];
    }
  }

  /**
   * Updates the repo selected and get the data for that particular repo selection
   * @param newRepo
   */
  updateRepo(newRepo: any) {
    this.selectedrepo = newRepo;
    this.wfIdWorkflows = { ...this.wfIdWorkflows, wfId: newRepo.value };
    this.route.navigate(["/workflow-run", this.wfIdWorkflows.wfId]);
    this.resetValues();
    this.getWfRunData();
  }

  /**
   * Updates the duration selected and get the data for that particular duration
   * @param newDuration
   */
  updateDuration(newDuration: any) {
    this.selectedDuration = newDuration;
    this.resetValues();
    this.getWfRunData();
  }

  /** 
   * Reset values is called when we update the repo or duration. It will reset the offset for infinite scroll
   * and the dataSource as well. Also cancels any pending API requests to prevent race conditions.
   */
  resetValues() {
    // Cancel any pending workflow run data request to prevent stale data from being appended
    if (this.wfRunDataSubscription) {
      this.wfRunDataSubscription.unsubscribe();
    }
    
    this.dataSource = this.filteredData = [];
    this.offset = 0;
    this.isLoadingMore = false;
    this.hasMoreData = true;
  }

  /**
   * This method is called when a dropdown selection is made by the user.
   */
  onDropdownSelect() {
    // Subscribe to the dropdownSelected$ observable provided by the cf.
    this.dataSubscription = this.cf.dropDownSelected$.subscribe((res: any) => {
      /**
       * Check if the dropdown selection is related to the repository dropdown.
       * @param {Object} res - The dropdown selection data.
       * @param {string} res.identifier - Identifier for the selected dropdown.
       */
      if (res.identifier === "repoDropdown") {
        this.updateRepo(res);
      }
      /**
       * Check if the dropdown selection is related to the duration dropdown.
       * @param {Object} res - The dropdown selection data.
       * @param {string} res.identifier - Identifier for the selected dropdown.
       */
      if (res.identifier === "durationDropdown") {
        // Call the updateDuration method to handle changes related to the duration.
        this.updateDuration(res);
      }
      this.rowData = null;
      this.searchClear();
    });
  }

  /**stgDtlMapping to map stage details with gantt chart jobs */
  stgDtlMapping() {
    this.stgDtl = {}; // Reset the stgDtl map

    this.qgDataSource.forEach((ds: any) => {
      ds.startTime = moment(ds.startTime);

      if (this.stgDtl.hasOwnProperty(ds.stageId)) {
        // If the stageId already exists in the map, concatenate the status
        this.stgDtl[ds.stageId] += `, ${ds.status}`;
      } else {
        this.stgDtl[ds.stageId] = ds.status;
      }
    });
    return this.stgDtl;
  }

  /**inputArgsMapping is used to map the key value pairs together */
  inputArgsMapping(response: any) {
    this.inputArgumentList = [];
    let temVal: any = [];
    this.inputArgumentList = Object.keys(response)
      .filter(key => !this.cf.isJSONObject(response[key]))
      .map(key => ({
        key,
        value: response[key]
      }));

    let key: any = Object.keys(response)
      .filter(key => this.cf.isJSONObject(response[key]))

    if (key.length > 0) {
      temVal = Object.entries(JSON.parse(response[key] || "{}")).map(key1 => ({
        key: key1[0],
        value: key1[1]
      }));
    }

    this.inputArgumentList = this.inputArgumentList.concat(temVal || [])
  }

  /****************************Api calls *****************************/

  /**adding the stage details from quality gates to gantt chart */
  qgToGanttChart() {
    this.featureWf.getWfQgRes(this.rowData.jobId).subscribe((response: any) => {
      this.qgDataSource = []
      if (response.data.dbResp && response.data.dbResp.length > 0) {
        this.qgDataSource = response.data.dbResp;
        this.stgDtlMapping();
      } else {
        this.stgDtl = {};
      }
    });
  }

  /**Input args api call */
  inputArgs() {
    this.wfQueueService.getInputArgs(this.rowData.jobId).subscribe((response: any) => {
      this.inputArgsMapping(response.data.dbResp);
    })
  }

  /**
   * Retrieves workflow run data based on the provided 'wfId'.
   * Stores subscription to allow cancellation if user changes workflow/duration before response arrives.
   * Uses finalize() to ensure isLoadingMore is always reset, even if exceptions occur.
   * @param {number} wfId - The identifier of the workflow for which to fetch run data.
   */
  getWfRunData() {
    if (this.isLoadingMore) return;
    
    this.isLoadingMore = true;
    
    // Store the subscription so we can cancel it if user switches workflow/duration
    this.wfRunDataSubscription = this.workflowRunService.getWfRun(
      this.selectedDuration.value, 
      this.wfIdWorkflows.wfId, 
      this.offset, 
      this.limit
    ).pipe(
      finalize(() => {
        // Always reset loading flag, even if error or exception occurs
        this.isLoadingMore = false;
      })
    ).subscribe((response: any) => {
      // Defensively ensure dbResp is an array to prevent .map() errors
      let res = response?.data?.dbResp;
      let safeRes = Array.isArray(res) ? res : [];
      
      let result = safeRes.map((el: any) => { 
        el = { ...el, triggeredOn: this.cf.getGMTtimeFormat(el.triggeredOn) }; 
        return el;
      });
      
      this.dataSource = [...this.dataSource, ...result];
      this.filteredData = this.dataSource;
      this.offset += this.limit;
      
      // Stop loading if we received less than requested (reached the end)
      if (result.length < this.limit) {
        this.hasMoreData = false;
      }
      
    }, (error) => { 
      console.error(Messages.workflow_run_http_error, error);
    });
  }
}
